import { Shape, ShapeType } from '@/services/shape';
import { useEditorStore } from '@/store/use-editor-store';
import { useLayerStore } from '@/store/use-layer-store';
import { cloneDeep } from 'lodash-es';
import { computed, ref } from 'vue';
import { useShapeManage } from './use-shape-manage';
import { ShapeTreeNode, useShapeTree } from './use-shape-tree';
import { dataURItoBlob } from '@/utils/fs/image.utils';
import saveAs from 'file-saver';
import { useWatchfaceScreenShot } from './use-watchface-screenshot';
import { canUse } from '@/utils/can-use.utils';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { emitter, EventType } from '@/utils/emitter.utils';

export const useLayerMenu = (options: {
  isOnCanvas: boolean;
  handleTemplateModal?: (shapeIds: string[]) => void;
  handleExportModal?: (shapeId: string) => void;
  handleRename?: () => void;
}) => {
  const editorStore = useEditorStore();
  const layerStore = useLayerStore();
  const { screenshot } = useWatchfaceScreenShot();
  const watchStore = useWatchfaceStore();

  const templateSelectedNodes = ref<
    {
      layer: StoreLayerItem;
      node: ShapeTreeNode;
    }[]
  >([]);

  const {
    copiedItems,
    hasPhotoAlbumBg,
    shapeTree,
    checkCanSloted,
    checkCanWidgeted,
    checkCanCopy,
    copy,
    cut,
    getNodeById,
    getParent,
    getSortedIds,
    paste
  } = useShapeTree();

  const { moveShape, sloted, unSloted, unWidgeted, widgeted, unGroup } =
    useShapeManage();

  function bringToFront() {
    const targetNodes = selectedNodes.value.filter(
      (node) => node.extra.tag !== ShapeType.PhotoAlbumBg
    );
    // 每个图层移到相邻同层级中的最前；多选时同层级相对顺序同移动前的相对顺序，非同层级各自处理
    for (let i = targetNodes.length - 1; i >= 0; i--) {
      const node = targetNodes[i];
      const parentNode = getParent(node);
      moveShape(
        node.key,
        editorStore.themeId,
        parentNode?.key,
        parentNode
          ? (parentNode.children?.length ?? 0) - 1
          : shapeTree.value.length - 1
      );
    }
  }

  function sendToBack() {
    const targetNodes = selectedNodes.value.filter(
      (node) => node.extra.tag !== ShapeType.PhotoAlbumBg
    );
    for (let i = 0; i < targetNodes.length; i++) {
      const node = targetNodes[i];
      const parentNode = getParent(node);

      moveShape(
        node.key,
        editorStore.themeId,
        parentNode?.key,
        !parentNode && hasPhotoAlbumBg.value ? 1 : 0
      );
    }
  }

  function createAstemplate() {
    //选中创建模板的图层，支持多选
    templateSelectedNodes.value = selectedNodes.value.reduce(
      (arr, node) => {
        const layer = layerStore.findLayer(node.key)?.layer;
        if (layer) {
          arr.push({
            node: cloneDeep(node),
            layer: cloneDeep(layer)
          });
        }
        return arr;
      },
      [] as {
        layer: StoreLayerItem;
        node: ShapeTreeNode;
      }[]
    );
    const shapeIds = [];
    for (let i = 0; i < templateSelectedNodes.value.length; i++) {
      shapeIds.push(templateSelectedNodes.value[i].node.key);
    }
    options.handleTemplateModal?.(shapeIds);
  }

  function showHide() {
    // 都是可见的话隐藏，有一个隐藏着的就全可见
    const isVisible = selectedNodes.value.some(
      (layer) => !layer.extra.visibility
    );
    editorStore.selectedIds.forEach((id) =>
      Shape.getShape(id)?.setAttrs({ _visibility: isVisible })
    );
  }

  function lockUnlock() {
    // 都是锁定的话解锁，有一个开着的就全锁上
    const targets = selectedNodes.value.filter(
      (node) => node.extra.tag !== ShapeType.PhotoAlbumBg
    );

    const isLock = targets.some((layer) => !layer.extra.locked);
    targets.forEach((target) =>
      Shape.getShape(target.key)?.setAttrs({ _isLocked: isLock })
    );
  }

  const selectedNodes = computed<ShapeTreeNode[]>(() =>
    getSortedIds(editorStore.selectedIds).map(
      (id) => getNodeById(id) as ShapeTreeNode
    )
  );

  const contextMenus = computed(() => {
    if (editorStore.selectedIds.some((id) => !getNodeById(id))) return [];

    // 全局蒙层只支持删除操作
    const isMaskOnly = selectedNodes.value.length > 0 &&
      selectedNodes.value.every((node) => Shape.getShape(node.key)?.attrs?._maskFlag);
    if (isMaskOnly) {
      return [
        {
          title: '删除',
          shortcuts: ['Backspace'],
          onClick() {
            editorStore.activeShapes.forEach((shape) => shape?.destroy());
          }
        }
      ] as DropDownMenuItem[];
    }

    const res: DropDownMenuItem[] = [];

    // 相册表盘背景不支持复制、剪切
    const specialWatchface = (node: ShapeTreeNode) =>
      [
        ShapeType.PhotoAlbumBg,
        ShapeType.TextWatchface,
        ShapeType.VideoWatchface
      ].includes(node.extra.tag) ||
      !!Shape.getShape(node.key)?.attrs?._maskFlag;
    if (checkCanCopy(selectedNodes.value)) {
      res.push(
        {
          title: '复制',
          shortcuts: ['CommandOrControl', 'C'],
          onClick: () => copy(selectedNodes.value)
        },
        {
          title: '剪切',
          shortcuts: ['CommandOrControl', 'X'],
          onClick: () => cut(selectedNodes.value)
        }
      );
    }

    if (copiedItems.value.length) {
      res.push({
        title: '粘贴',
        shortcuts: ['CommandOrControl', 'V'],
        onClick: paste
      });
    }

    if (res.length && selectedNodes.value.length) {
      res.push({ delimiter: true });
    }

    if (selectedNodes.value.some((item) => !specialWatchface(item))) {
      res.push(
        {
          title: '移到最前',
          shortcuts: [']'],
          onClick: bringToFront
        },
        {
          title: '移到最后',
          shortcuts: ['['],
          onClick: sendToBack
        },

        { delimiter: true }
      );
    }

    if (
      selectedNodes.value.length &&
      selectedNodes.value.every((item) => !specialWatchface(item))
    ) {
      res.push(
        {
          title: '创建为模板',
          onClick: createAstemplate
        },

        { delimiter: true }
      );
    }

    if (checkCanWidgeted(selectedNodes.value)) {
      res.push({
        title: '成组',
        shortcuts: ['CommandOrControl', 'G'],
        onClick() {
          widgeted(
            editorStore.selectedIds,
            editorStore.themeId,
            getNodeById(editorStore.selectedIds[0])?.ancestryInfo?.nodes.at(-1)
              ?.key
          );
        }
      });
    }

    // 被解组的可能为 组 或 父元素不为容器的表达式
    const needUngroup = selectedNodes.value.filter(
      (item) =>
        item.extra.tag == ShapeType.Group ||
        (item.extra.tag === ShapeType.Widget &&
          getParent(item)?.extra.tag !== ShapeType.Slot)
    );

    if (needUngroup.length) {
      res.push({
        title: '解组',
        shortcuts: ['CommandOrControl', 'Backspace'],
        onClick() {
          needUngroup.forEach((node) => {
            if (node.extra.tag === ShapeType.Group) {
              unGroup(node.key, editorStore.themeId);
            } else {
              unWidgeted(node.key, editorStore.themeId);
            }
          });
          editorStore.unselect();
        }
      });
    }

    if (
      import.meta.env.DEV &&
      editorStore.selectedIds.length == 1 &&
      !specialWatchface(selectedNodes.value[0])
    ) {
      res.push({
        title: '导出图层(ONLY DEV)',
        async onClick() {
          options.handleExportModal?.(editorStore.selectedIds[0]);
        }
      });
    }

    if (checkCanSloted(selectedNodes.value)) {
      if (
        canUse('support-slot-following').valid &&
        watchStore.slotFollowing &&
        editorStore.theme?.id !== layerStore.themes[0].id
      ) {
        const firstThemeSlots =
          layerStore.themes[0].children.filter(
            (item) => item.type === ShapeType.Slot
          ) || [];
        if (firstThemeSlots.length > 0) {
          const inheritMenuItems: DropDownMenuItem[] = [
            // 继承容器选项
            ...firstThemeSlots.map((slot) => ({
              title: `跟随亮屏->样式1 ${slot.attrs.name || '未命名'}`,
              onClick() {
                sloted(editorStore.selectedIds, editorStore.themeId, slot.id);
              }
            })),
            // 分隔线
            { delimiter: true },
            // 不继承选项
            {
              title: '不跟随，直接创建',
              onClick() {
                sloted(editorStore.selectedIds, editorStore.themeId);
              }
            }
          ];
          res.push({
            title: '创建容器',
            shortcuts: ['Alt', 'CommandOrControl', 'K'],
            children: inheritMenuItems
          });
        } else {
          res.push({
            title: '创建容器',
            shortcuts: ['Alt', 'CommandOrControl', 'K'],
            onClick() {
              sloted(editorStore.selectedIds, editorStore.themeId);
            }
          });
        }
      } else {
        res.push({
          title: '创建容器',
          shortcuts: ['Alt', 'CommandOrControl', 'K'],
          onClick() {
            sloted(editorStore.selectedIds, editorStore.themeId);
          }
        });
      }
    }

    // 解除容器：选中项里有容器
    const slots = selectedNodes.value.filter(
      (node) => node.extra.tag === ShapeType.Slot
    );
    if (slots.length) {
      res.push({
        title: '解除容器',
        onClick() {
          slots.forEach((node) => unSloted(node.key, editorStore.themeId));
          editorStore.unselect();
        }
      });
    }

    // 重命名 (在画布上不显示此选项)
    if (
      !options.isOnCanvas &&
      selectedNodes.value.length === 1 &&
      !specialWatchface(selectedNodes.value[0])
    ) {
      res.push({
        title: '重命名',
        shortcuts: ['CommandOrControl', 'R'],
        onClick: options.handleRename
      });
    }

    if (selectedNodes.value.some((item) => !specialWatchface(item))) {
      res.push({
        title: '删除',
        shortcuts: ['Backspace'],
        onClick() {
          editorStore.activeShapes
            .filter(
              (shape) =>
                ![
                  ShapeType.PhotoAlbumBg,
                  ShapeType.TextWatchface,
                  ShapeType.VideoWatchface
                ].includes(shape!.type)
            )
            .forEach((shape) => shape?.destroy());
        }
      });
    }

    if (res.length && selectedNodes.value.length) {
      res.push({ delimiter: true });
    }

    if (selectedNodes.value.length) {
      res.push({
        title: '隐藏/显示',
        shortcuts: ['Shift', 'CommandOrControl', 'H'],
        onClick: showHide
      });
    }

    if (selectedNodes.value.some((item) => !specialWatchface(item))) {
      res.push({
        title: '锁定/解锁',
        shortcuts: ['Shift', 'CommandOrControl', 'L'],
        onClick: lockUnlock
      });
    }

    // 如果选择了一个图层可以显示截图功能
    if (editorStore.selectedIds.length == 1) {
      res.push({
        title: '图层截图',
        onClick: async () => {
          const shape = Shape.getShape(editorStore.selectedIds[0]);

          const themeName = editorStore.theme?.name;
          const themeType = editorStore.theme?.type;
          const themeColor = editorStore.themeColor?.color;
          const dataURI = await screenshot(undefined, {
            usingCurConfigData: true,
            themeName: themeName,
            themeType: themeType,
            themeColor,
            ignoreBg: true,
            shapeIds: editorStore.selectedIds
          });
          if (!dataURI) return;
          const blob = dataURItoBlob(dataURI as string);
          const name = `图层_${themeName}_${editorStore.themeColor?.name}_${shape?.attrs.name}.png`;
          saveAs(blob, name);
        }
      });
    }

    return res;
  });

  return { contextMenus };
};
